import type { ClaimFlowDraft } from './claimDraftStorage'
import { sanitizeRegistrationNumber } from '../utils/validation'

/** Extra other-driver blocks allowed in addition to the first (3 drivers total). */
export const MAX_EXTRA_MULTI_VEHICLE_OTHER_DRIVERS = 2

export type MultiVehicleOtherPartyDriverIndex = 1 | 2 | 3

export interface MultiVehicleOtherPartyDriverFields {
  name: string
  address: string
  phone: string
  registration: string
}

const MULTI_VEHICLE_OTHER_PARTY_PRIMARY_STRING_KEYS = [
  'multiVehicleOtherPartyName',
  'multiVehicleOtherPartyAddress',
  'multiVehicleOtherPartyDobDdMmYyyy',
  'multiVehicleOtherPartyPhone',
  'multiVehicleOtherPartyRegistration',
  'multiVehicleOtherPartyLicenceNumber',
] as const satisfies readonly (keyof ClaimFlowDraft)[]

/** Draft string keys for additional other-driver slots (validation and copy lists). */
export const MULTI_VEHICLE_OTHER_PARTY_EXTRA_DRIVER_STRING_KEYS = [
  'multiVehicleOtherParty2Name',
  'multiVehicleOtherParty2Address',
  'multiVehicleOtherParty2Phone',
  'multiVehicleOtherParty2Registration',
  'multiVehicleOtherParty3Name',
  'multiVehicleOtherParty3Address',
  'multiVehicleOtherParty3Phone',
  'multiVehicleOtherParty3Registration',
] as const satisfies readonly (keyof ClaimFlowDraft)[]

/** All other-party string fields validated on {@link ClaimFlowDraft}. */
export const MULTI_VEHICLE_OTHER_PARTY_STRING_KEYS = [
  ...MULTI_VEHICLE_OTHER_PARTY_PRIMARY_STRING_KEYS,
  ...MULTI_VEHICLE_OTHER_PARTY_EXTRA_DRIVER_STRING_KEYS,
] as const satisfies readonly (keyof ClaimFlowDraft)[]

export type MultiVehicleOtherPartyRailSummaryInput = Pick<
  ClaimFlowDraft,
  'multiVehicleOtherPartyDetailsKnown' | (typeof MULTI_VEHICLE_OTHER_PARTY_STRING_KEYS)[number]
>

type OtherPartyDriverFieldKey =
  | 'multiVehicleOtherPartyName'
  | 'multiVehicleOtherPartyAddress'
  | 'multiVehicleOtherPartyPhone'
  | 'multiVehicleOtherPartyRegistration'
  | 'multiVehicleOtherParty2Name'
  | 'multiVehicleOtherParty2Address'
  | 'multiVehicleOtherParty2Phone'
  | 'multiVehicleOtherParty2Registration'
  | 'multiVehicleOtherParty3Name'
  | 'multiVehicleOtherParty3Address'
  | 'multiVehicleOtherParty3Phone'
  | 'multiVehicleOtherParty3Registration'

const DRIVER_FIELD_KEYS: Record<
  MultiVehicleOtherPartyDriverIndex,
  {
    name: OtherPartyDriverFieldKey
    address: OtherPartyDriverFieldKey
    phone: OtherPartyDriverFieldKey
    registration: OtherPartyDriverFieldKey
  }
> = {
  1: {
    name: 'multiVehicleOtherPartyName',
    address: 'multiVehicleOtherPartyAddress',
    phone: 'multiVehicleOtherPartyPhone',
    registration: 'multiVehicleOtherPartyRegistration',
  },
  2: {
    name: 'multiVehicleOtherParty2Name',
    address: 'multiVehicleOtherParty2Address',
    phone: 'multiVehicleOtherParty2Phone',
    registration: 'multiVehicleOtherParty2Registration',
  },
  3: {
    name: 'multiVehicleOtherParty3Name',
    address: 'multiVehicleOtherParty3Address',
    phone: 'multiVehicleOtherParty3Phone',
    registration: 'multiVehicleOtherParty3Registration',
  },
}

export function formatOtherPartyDriverReviewLine(
  draft: MultiVehicleOtherPartyRailSummaryInput,
  index: MultiVehicleOtherPartyDriverIndex
): string | null {
  const fields = readOtherPartyDriverFromDraft(draft, index)
  if (!otherPartyDriverHasAnyData(fields)) return null
  const parts: string[] = []
  if (fields.name.trim()) parts.push(fields.name.trim())
  if (fields.address.trim()) parts.push(fields.address.trim())
  if (fields.phone.trim()) parts.push(fields.phone.trim())
  if (fields.registration.trim()) parts.push(fields.registration.trim())
  const label = index === 1 ? null : `Driver ${index}`
  return [label, ...parts].filter(Boolean).join(' · ')
}

export function readOtherPartyDriverFromDraft(
  draft: MultiVehicleOtherPartyRailSummaryInput | null | undefined,
  index: MultiVehicleOtherPartyDriverIndex
): MultiVehicleOtherPartyDriverFields {
  const keys = DRIVER_FIELD_KEYS[index]
  const name = draft?.[keys.name]
  const address = draft?.[keys.address]
  const phone = draft?.[keys.phone]
  const registration = draft?.[keys.registration]
  return {
    name: typeof name === 'string' ? name : '',
    address: typeof address === 'string' ? address : '',
    phone: typeof phone === 'string' ? phone : '',
    registration: typeof registration === 'string' ? registration : '',
  }
}

export function otherPartyDriverHasAnyData(fields: MultiVehicleOtherPartyDriverFields): boolean {
  return Boolean(
    fields.name.trim() ||
      fields.address.trim() ||
      fields.phone.trim() ||
      fields.registration.trim()
  )
}

/** How many extra driver blocks (beyond the first) should be visible from saved draft. */
export function getExtraOtherDriverSlotCount(
  draft: MultiVehicleOtherPartyRailSummaryInput | null | undefined
): 0 | 1 | 2 {
  if (otherPartyDriverHasAnyData(readOtherPartyDriverFromDraft(draft, 3))) {
    return 2
  }
  if (otherPartyDriverHasAnyData(readOtherPartyDriverFromDraft(draft, 2))) {
    return 1
  }
  return 0
}

export function countOtherPartyDriversWithData(
  draft: MultiVehicleOtherPartyRailSummaryInput | null | undefined
): number {
  let count = 0
  for (const index of [1, 2, 3] as const) {
    if (otherPartyDriverHasAnyData(readOtherPartyDriverFromDraft(draft, index))) {
      count += 1
    }
  }
  return count
}

export function buildOtherPartyDriverPersistPatch(
  index: MultiVehicleOtherPartyDriverIndex,
  fields: MultiVehicleOtherPartyDriverFields
): Partial<ClaimFlowDraft> {
  const keys = DRIVER_FIELD_KEYS[index]
  const regoRaw = fields.registration.trim()
  const regoStored = regoRaw ? sanitizeRegistrationNumber(regoRaw) || null : null
  return {
    [keys.name]: fields.name.trim() || null,
    [keys.address]: fields.address.trim() || null,
    [keys.phone]: fields.phone.trim() || null,
    [keys.registration]: regoStored,
  } as Partial<ClaimFlowDraft>
}

/** Clears optional additional other-driver slots (e.g. when user answers No). */
export function clearExtraOtherPartyDriversPatch(): Partial<ClaimFlowDraft> {
  return {
    multiVehicleOtherParty2Name: null,
    multiVehicleOtherParty2Address: null,
    multiVehicleOtherParty2Phone: null,
    multiVehicleOtherParty2Registration: null,
    multiVehicleOtherParty3Name: null,
    multiVehicleOtherParty3Address: null,
    multiVehicleOtherParty3Phone: null,
    multiVehicleOtherParty3Registration: null,
  }
}

function appendDriverLines(block: string, index: MultiVehicleOtherPartyDriverIndex, draft: ClaimFlowDraft): string {
  const fields = readOtherPartyDriverFromDraft(draft, index)
  if (!otherPartyDriverHasAnyData(fields)) return block
  if (index > 1) {
    block += `\n\n--- Other involved driver ${index} (portal) ---`
  }
  const n = fields.name.trim()
  if (n) block += `\nName: ${n}`
  const a = fields.address.trim()
  if (a) block += `\nAddress: ${a}`
  const p = fields.phone.trim()
  if (p) block += `\nPhone: ${p}`
  const r = fields.registration.trim()
  if (r) block += `\nVehicle registration: ${r}`
  return block
}

/** FNOL appendix for all other involved drivers captured in the portal. */
export function formatOtherPartyDriversAppendix(draft: ClaimFlowDraft): string {
  let block = '\n\n--- Other involved driver (portal) ---'
  const known = draft.multiVehicleOtherPartyDetailsKnown
  block += `\nHas details of other driver: ${known === true ? 'Yes' : known === false ? 'No' : '—'}`
  if (known !== true) return block
  block = appendDriverLines(block, 1, draft)
  block = appendDriverLines(block, 2, draft)
  block = appendDriverLines(block, 3, draft)
  return block
}
